import { lazy } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";

import { ProtectedRoute } from "./auth/ProtectedRoute";
import { AppLayout } from "./components/AppLayout";
import { IMPLEMENTED_PATHS, NAV_ITEMS } from "./nav";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PlaceholderPage } from "./pages/PlaceholderPage";

// TD-0047: route-level code splitting — кожна важка сторінка вантажиться окремим
// chunk-ом на навігації (не у стартовому бандлі). Shell/login/404/placeholder —
// eager (дрібні, потрібні одразу). Межа Suspense — навколо Outlet в AppLayout.
const ArchiveListPage = lazy(() => import("./pages/ArchiveListPage").then((m) => ({ default: m.ArchiveListPage })));
const ClientsListPage = lazy(() => import("./pages/ClientsListPage").then((m) => ({ default: m.ClientsListPage })));
const ChatPage = lazy(() => import("./pages/ChatPage").then((m) => ({ default: m.ChatPage })));
const ClientCardPage = lazy(() => import("./pages/ClientCardPage").then((m) => ({ default: m.ClientCardPage })));
const ContactsPage = lazy(() => import("./pages/ContactsPage").then((m) => ({ default: m.ContactsPage })));
const CreateWizardPage = lazy(() => import("./pages/CreateWizardPage").then((m) => ({ default: m.CreateWizardPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const DictionariesBrowsePage = lazy(() => import("./pages/DictionariesBrowsePage").then((m) => ({ default: m.DictionariesBrowsePage })));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage").then((m) => ({ default: m.HelpCenterPage })));
const HelpArticlePage = lazy(() => import("./pages/HelpArticlePage").then((m) => ({ default: m.HelpArticlePage })));
const HelpTopicPage = lazy(() => import("./pages/HelpTopicPage").then((m) => ({ default: m.HelpTopicPage })));
const HelpManagePage = lazy(() => import("./pages/HelpManagePage").then((m) => ({ default: m.HelpManagePage })));
const IntegrationRunbookPage = lazy(() => import("./pages/IntegrationRunbookPage").then((m) => ({ default: m.IntegrationRunbookPage })));
const DocumentCardPage = lazy(() => import("./pages/DocumentCardPage").then((m) => ({ default: m.DocumentCardPage })));
const DocumentsListPage = lazy(() => import("./pages/DocumentsListPage").then((m) => ({ default: m.DocumentsListPage })));
const LibraryPage = lazy(() => import("./pages/LibraryPage").then((m) => ({ default: m.LibraryPage })));
const NotificationPreferencesPage = lazy(() => import("./pages/NotificationPreferencesPage").then((m) => ({ default: m.NotificationPreferencesPage })));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const ProjectCardPage = lazy(() => import("./pages/ProjectCardPage").then((m) => ({ default: m.ProjectCardPage })));
const ProjectsListPage = lazy(() => import("./pages/ProjectsListPage").then((m) => ({ default: m.ProjectsListPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const SearchPage = lazy(() => import("./pages/SearchPage").then((m) => ({ default: m.SearchPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const TaskCardPage = lazy(() => import("./pages/TaskCardPage").then((m) => ({ default: m.TaskCardPage })));
const ProcessCardPage = lazy(() => import("./pages/ProcessCardPage").then((m) => ({ default: m.ProcessCardPage })));
const ProcessesListPage = lazy(() => import("./pages/ProcessesListPage").then((m) => ({ default: m.ProcessesListPage })));
const ProcessStartWizard = lazy(() => import("./pages/ProcessStartWizard").then((m) => ({ default: m.ProcessStartWizard })));
const TasksListPage = lazy(() => import("./pages/TasksListPage").then((m) => ({ default: m.TasksListPage })));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage").then((m) => ({ default: m.UserProfilePage })));

// Реалізовані розділи — `IMPLEMENTED_PATHS` тепер живе у `./nav` (спільне джерело
// істини для nav-плейсхолдерів і контракт-тесту довідки NAV_ITEM↔help-topic).

// /tasks/new — bookmark/ярлик дошки проєкту. ЗБЕРІГАЄМО query (project_id, document_id,
// owner_id, client, …) при редіректі на wizard. Без цього `<Navigate to="/create?kind=task">`
// губив `?project_id=` → задача з дошки проєкту створювалась без привязки до проєкту.
function TasksNewRedirect() {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set("kind", "task");
  return <Navigate to={`/create?${next.toString()}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/create" element={<CreateWizardPage />} />
          <Route path="/documents" element={<DocumentsListPage />} />
          {/* TD-0059: єдиний create-флоу — wizard. Legacy-маршрути редіректять (bookmarks). */}
          <Route path="/documents/new" element={<Navigate to="/create?kind=document" replace />} />
          <Route path="/documents/:id" element={<DocumentCardPage />} />
          <Route path="/processes" element={<ProcessesListPage />} />
          <Route path="/processes/new" element={<ProcessStartWizard />} />
          <Route path="/processes/:id" element={<ProcessCardPage />} />
          <Route path="/tasks" element={<TasksListPage />} />
          <Route path="/tasks/new" element={<TasksNewRedirect />} />
          <Route path="/tasks/:id" element={<TaskCardPage />} />
          <Route path="/projects" element={<ProjectsListPage />} />
          <Route path="/projects/:id" element={<ProjectCardPage />} />
          <Route path="/archive" element={<ArchiveListPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:conversationId" element={<ChatPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/clients" element={<ClientsListPage />} />
          <Route path="/clients/:id" element={<ClientCardPage />} />
          <Route path="/dictionaries" element={<DictionariesBrowsePage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/help/manage" element={<HelpManagePage />} />
          <Route path="/help/topics/:topicId" element={<HelpTopicPage />} />
          <Route path="/help/articles/:slugOrId" element={<HelpArticlePage />} />
          {/* M15 (ADR-041): runbook інтеграції — для будь-якого автентифікованого користувача DMS. */}
          <Route path="/integration-runbook" element={<IntegrationRunbookPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile" element={<UserProfilePage />} />
          {NAV_ITEMS.filter((item) => !IMPLEMENTED_PATHS.has(item.path)).map((item) => (
            <Route
              key={item.path}
              path={item.path}
              element={<PlaceholderPage title={item.label} />}
            />
          ))}
          <Route path="/settings/notifications" element={<NotificationPreferencesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
